import { isPointsApiSuccess } from "@/utils/mallGoods"
import { openWithEntry } from "@/utils/url"

export const MALL_SITE_COOKIE_KEY = "xcoins_mall_site"

const PREFERRED_STATIONS = ["CN", "US", "DE", "JP"]
const SITE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 手动站点保留一年

let currentMallCountry = ""
let currentMallStation = ""
let currentMallSource = ""
let siteReady = false
let sitePromise = null

let cachedCountries = null
let cachedStations = null
let regionsPromise = null
let regionsVersion = 0 // 语种切换后作废进行中的地区请求

export function getMallCountry() {
  return currentMallCountry || ""
}

export function setMallCountry(countryCode) {
  currentMallCountry = String(countryCode || "").toUpperCase()
  return currentMallCountry
}

export function getMallStation() {
  return currentMallStation || ""
}

export function setMallStation(stationCode) {
  currentMallStation = String(stationCode || "").toUpperCase()
  return currentMallStation
}

export function getMallSiteSource() {
  return currentMallSource || ""
}

export function applyMallSiteState({ country, station, source } = {}) {
  currentMallCountry = String(country || "").toUpperCase()
  currentMallStation = String(station || "").toUpperCase()
  currentMallSource = source || ""
  siteReady = true
  return {
    country: currentMallCountry,
    station: currentMallStation,
    source: currentMallSource,
  }
}

/** 只读取用户手动选择的站点；IP 识别结果不落 cookie */
export function readStoredMallSite(cookies) {
  if (!cookies) {
    return null
  }

  try {
    const parsed = cookies.get(MALL_SITE_COOKIE_KEY)
    if (!parsed || typeof parsed !== "object") {
      return null
    }
    if (parsed.source === "ip") {
      return null
    }

    const country = String(parsed.country || "").toUpperCase()
    const station = String(parsed.station || "").toUpperCase()
    if (!country && !station) {
      return null
    }

    return {
      country,
      station,
      source: "manual",
    }
  } catch (e) {
    return null
  }
}

export function writeStoredMallSite({ country, station, source }, cookies) {
  if (!cookies || source === "ip") {
    return
  }

  const payload = {
    country: String(country || "").toUpperCase(),
    station: String(station || "").toUpperCase(),
    source: "manual",
  }
  if (!payload.country && !payload.station) {
    return
  }
  cookies.set(MALL_SITE_COOKIE_KEY, payload, {
    path: "/",
    maxAge: SITE_COOKIE_MAX_AGE,
  })
}

export function applyManualMallSite({ country, station } = {}, cookies) {
  const resolved = applyMallSiteState({
    country,
    station,
    source: "manual",
  })
  writeStoredMallSite(resolved, cookies)
  return resolved
}

/**
 * 仅客户端把手动站点写入模块缓存。
 * 服务端不能写 siteReady，否则后续 SSR 请求会沿用上一位访客的 country / station。
 */
export function hydrateMallSiteFromCookies(cookies) {
  if (process.server || siteReady || !cookies) {
    return
  }
  const storedSite = readStoredMallSite(cookies)
  if (storedSite && (storedSite.country || storedSite.station)) {
    applyMallSiteState(storedSite)
  }
}

export function flattenMallCountries(regionList) {
  const countries = []
  const seen = new Set()

  ;(Array.isArray(regionList) ? regionList : []).forEach((group) => {
    ;(Array.isArray(group?.countries) ? group.countries : []).forEach((item) => {
      const code = String(item?.code || "").toUpperCase()
      if (!code || seen.has(code)) {
        return
      }

      seen.add(code)
      countries.push({
        code,
        name: item.name || code,
        stationCode: String(item.stationCode || code).toUpperCase(),
        domain: String(item.domain || "").trim(),
        region: group.region || "",
        subRegion: group.subRegion || "",
      })
    })
  })

  return countries
}

export function buildMallStationOptions(countries) {
  const stationMap = new Map()

  countries.forEach((country) => {
    const stationCode = country.stationCode || country.code
    if (!stationMap.has(stationCode)) {
      stationMap.set(stationCode, {
        stationCode,
        primaryCountry: country.code,
        name: country.name,
        countries: [],
      })
    }

    const station = stationMap.get(stationCode)
    station.countries.push(country)
    if (country.code === stationCode) {
      station.primaryCountry = country.code
      station.name = country.name
    }
  })

  const stations = Array.from(stationMap.values())
  stations.sort((prev, next) => {
    const prevIndex = PREFERRED_STATIONS.indexOf(prev.stationCode)
    const nextIndex = PREFERRED_STATIONS.indexOf(next.stationCode)
    if (prevIndex === -1 && nextIndex === -1) {
      return prev.stationCode.localeCompare(next.stationCode)
    }
    if (prevIndex === -1) {
      return 1
    }
    if (nextIndex === -1) {
      return -1
    }
    return prevIndex - nextIndex
  })

  return stations
}

export function findCountryInList(countries, countryCode) {
  const code = String(countryCode || "").toUpperCase()
  return countries.find((item) => item.code === code) || null
}

export function findStationInList(stations, stationCode) {
  const code = String(stationCode || "").toUpperCase()
  return stations.find((item) => item.stationCode === code) || null
}

export async function detectCountryByIp() {
  if (!process.client) {
    return ""
  }

  const sources = [
    async () => {
      const res = await fetch("https://api.country.is/", { cache: "no-store" })
      const data = await res.json()
      return data?.country || ""
    },
    async () => {
      const res = await fetch("https://ipwho.is/?fields=country_code", { cache: "no-store" })
      const data = await res.json()
      return data?.country_code || ""
    },
  ]

  for (const request of sources) {
    try {
      const country = String((await request()) || "").toUpperCase()
      if (country) {
        return country
      }
    } catch (e) {}
  }

  return ""
}

/** 页脚切语种后清空地区缓存，下次按新 Accept-Language 重拉 */
export function invalidateMallRegions() {
  cachedCountries = null
  cachedStations = null
  regionsPromise = null
  regionsVersion += 1
}

export async function ensureMallRegions($global) {
  if (cachedCountries && cachedStations) {
    return {
      countries: cachedCountries,
      stations: cachedStations,
    }
  }

  if (!regionsPromise) {
    const fetchId = regionsVersion
    regionsPromise = (async () => {
      let countries = []
      try {
        const res = await $global.pointsMallRegions().catch(() => null)
        if (isPointsApiSuccess(res) && Array.isArray(res.data)) {
          countries = flattenMallCountries(res.data)
        }
      } catch (e) {}

      // 切语种后旧请求作废，避免写回过期国家名
      if (fetchId !== regionsVersion) {
        return regionsPromise
      }

      cachedCountries = countries
      cachedStations = buildMallStationOptions(countries)
      return {
        countries: cachedCountries,
        stations: cachedStations,
      }
    })()
  }

  return regionsPromise
}

/** 强制按当前语种重新拉取国家/站点 */
export async function refreshMallRegions($global) {
  invalidateMallRegions()
  return ensureMallRegions($global)
}

/** 按本次 Cookie 解析站点，不写入模块变量 */
async function resolveEffectiveSite(cookies) {
  const storedSite = readStoredMallSite(cookies)
  if (storedSite && (storedSite.country || storedSite.station)) {
    return {
      country: storedSite.country,
      station: storedSite.station || storedSite.country,
      source: "manual",
    }
  }

  const ipCountry = await detectCountryByIp()
  if (ipCountry) {
    return {
      country: ipCountry,
      station: "",
      source: "ip",
    }
  }

  return {
    country: "",
    station: "",
    source: "",
  }
}

function currentMallSite() {
  return {
    country: currentMallCountry,
    station: currentMallStation,
    source: currentMallSource,
  }
}

/** 站点优先级：本地手动选择 > IP 解析 > 无站点。服务端每次按入参 Cookie 解析，不复用模块缓存 */
export async function ensureMallSite(cookies) {
  if (process.server) {
    return resolveEffectiveSite(cookies)
  }

  if (siteReady) {
    return currentMallSite()
  }

  if (!sitePromise) {
    sitePromise = resolveEffectiveSite(cookies).then((site) => applyMallSiteState(site))
  }

  return sitePromise
}

export function resolveMallSite({ countries, stations, storedSite, ipCountry }) {
  if (storedSite && (storedSite.country || storedSite.station)) {
    const storedCountry = findCountryInList(countries, storedSite.country)
    if (storedCountry) {
      return {
        country: storedCountry.code,
        station: storedCountry.stationCode,
        source: "manual",
      }
    }

    const storedStation = findStationInList(stations, storedSite.station)
    if (storedStation) {
      return {
        country: storedStation.primaryCountry,
        station: storedStation.stationCode,
        source: "manual",
      }
    }

    return {
      country: storedSite.country || "",
      station: storedSite.station || storedSite.country || "",
      source: "manual",
    }
  }

  const ipMatched = findCountryInList(countries, ipCountry)
  if (ipMatched) {
    return {
      country: ipMatched.code,
      station: ipMatched.stationCode,
      source: "ip",
    }
  }

  if (ipCountry) {
    return {
      country: String(ipCountry || "").toUpperCase(),
      station: "",
      source: "ip",
    }
  }

  return {
    country: "",
    station: "",
    source: "",
  }
}

/** 站点域名补全协议，只认 regions 返回的 domain */
export function normalizeMallSiteUrl(domain) {
  const value = String(domain || "").trim()
  if (!value) {
    return ""
  }
  if (/^https?:\/\//i.test(value)) {
    return value
  }
  return `https://${value}`
}

/**
 * 用当前选中的商城国家，从 regions 取该国站点域名。
 * @returns {Promise<string>} 可打开的绝对地址，没有 domain 时为空字符串
 */
export async function resolveSelectedMallSiteDomain($global, cookies) {
  const site = await ensureMallSite(cookies)
  const { countries } = await ensureMallRegions($global)
  const country = findCountryInList(countries, site.country)
  return normalizeMallSiteUrl(country && country.domain)
}

/** 在新标签打开选中国家的商城站点 */
export function openMallCouponSite(url) {
  if (!url) {
    return
  }
  openWithEntry(url, "xcoins_coupon")
}
